import {createI18n} from "vue-i18n";
import {defineAsyncComponent, inject} from "vue";
import moment from "moment";

class Locale {
    app;
    instance;
    emitter;
    lang;
    locale;
    weekStart;
    timeFormat;
    dateFormat;
    timePeriod;
    rtlLoaded = false;

    constructor(app, { messages, lang, locale, dir, week_start_day = 0, time_format, date_format  }) {
        this.app = app;
        this.emitter = app.config.globalProperties.$emitter;
        this.lang = lang;
        this.locale = locale;

        if (typeof week_start_day !== 'undefined') {
            this.weekStart = week_start_day;
            this.timeFormat = this.fixTimeFormat(time_format);
            this.dateFormat = this.fixDateFormat(date_format);
            this.setupMoment();
        }

        this.instance = createI18n({
            legacy: false,
            locale: lang,
            messages,
            globalInjection: true,
        });
        app.use(this.instance);

        this.loadStyles(dir);
    }

    set(data, emit) {
        if(data?.locale && data?.messages) {
            this.locale = data.locale;
            this.lang = data.lang;
            this.instance.global.messages.value[data.lang] = data.messages;
            this.instance.global.locale.value = data.lang;
            this.loadStyles(data.dir);
        }

        this.weekStart = data?.week_start_day || this.weekStart;
        this.timeFormat = this.fixTimeFormat(data?.time_format || this.timeFormat);
        this.dateFormat = this.fixDateFormat(data?.date_format || this.dateFormat);

        this.setupMoment();

        if (emit) {
            this.emitter.emit('locale:change');
        }
    }

    updateFromUser() {
        const store = this.app.config.globalProperties.$store;
        const user = store.getters['auth/user'];

        this.set({
            week_start_day: user.week_start_day,
            time_format: user.time_format,
            date_format: user.date_format,
        });
    }

    fixTimeFormat(format) {
        this.timePeriod = format.split(' ').length > 1;
        return format
            .replace('H', this.timePeriod > 1 ? 'H' : 'HH')
            .replace('i', 'mm')
            .replace('s', 'ss');
    }

    fixDateFormat(format) {
        return format.replace('d', 'DD')
            .replace('m', 'MM')
            .replace('Y', 'yyyy');
    }

    setupMoment() {
        const timeFormat = this.timeFormat.split(':');
        const shortTimeFormat = `${timeFormat[0]}:${timeFormat[1]}${this.timePeriod ? ' A' : ''}`;

        const longDateFormat = {
            LT: shortTimeFormat, // LT: 'HH:mm'
            LTS: timeFormat.join(':'), // LTS: 'HH:mm:ss'
            L: this.dateFormat, // L: 'YYYY-MM-DD'
            LL: this.dateFormat, // LL: 'YYYY MMMM D'
            LLL: `${this.dateFormat} ${shortTimeFormat}`, // LLL: 'YYYY MMMM D HH:mm',
            LLLL: `${this.dateFormat}, ${shortTimeFormat}` // LLLL: 'YYYY MMMM D, dddd HH:mm'
        }
        moment.updateLocale(this.locale, {
            week: { dow: this.weekStart },
            longDateFormat,
        });

        moment.locale(this.locale);
    }

    async loadStyles(dir) {
        document.querySelector('html')
            .setAttribute('direction', dir);

        if (dir === 'rtl' && !this.rtlLoaded) {
            await import('@/css/rtl.scss');
            this.rtlLoaded = true;
        }
    }
}

export default {
    install(app, options) {
        const locale = new Locale(app, options);
        app.config.globalProperties.$locale = locale;
        app.provide('locale', locale);
    }
}

export function useLocale() {
    return inject('locale');
}